import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { fetchStorageTariff } from '../services/api';
import { calculateStorageFee, StorageTariff } from '../services/storage-tariff-model';
import { downloadStoragePdf, storagePdfFileName } from '../utils/storagePdf';
import logoColor from './assets/adana_renkli.svg';
import iconCalendar from './assets/icon-calendar.png';
import iconHexagon from './assets/icon-hexagon.png';
import './StorageCalculator.css';

const CONTAINERS = [
  { value: '20', title: "20'", subtitle: 'Standart' },
  { value: '40', title: "40'/45'", subtitle: 'Standart / High Cube' },
] as const;

function today() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const money = (value: number) => value.toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function StorageCalculator() {
  const { t, locale } = useLanguage();
  const [tariff, setTariff] = useState<StorageTariff | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [size, setSize] = useState<'20' | '40'>('40');
  const [serviceKey, setServiceKey] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const resultPanel = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  // Sitedeki gorunumun aynisi, girilen degerlerle A4 PDF olarak indirilir (utils/storagePdf)
  const handlePdf = async () => {
    const section = sectionRef.current;
    if (!section || pdfBusy) return;
    setPdfBusy(true);
    setPdfError(false);
    try {
      await downloadStoragePdf(section, {
        logoSrc: logoColor,
        logoAlt: 'TBH Port Adana',
        fileName: storagePdfFileName(startDate, endDate),
        title: `TBH Port Adana - ${t('Ardiye Hesaplayıcı')}`,
      });
    } catch {
      setPdfError(true);
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    let disposed = false;
    let requestNumber = 0;
    let controller: AbortController | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    async function refresh() {
      const currentRequest = ++requestNumber;
      controller?.abort();
      clearTimeout(timeout);
      controller = new AbortController();
      timeout = setTimeout(() => controller?.abort(), 12000);
      setLoading(true);
      setLoadError(false);
      try {
        const nextTariff = await fetchStorageTariff(controller.signal);
        if (disposed || currentRequest !== requestNumber) return;
        setTariff(nextTariff);
        setServiceKey((current) => nextTariff.services.some((service) => service.enabled && service.key === current)
          ? current
          : nextTariff.services.filter((service) => service.enabled).sort((a, b) => a.sortOrder - b.sortOrder)[0]?.key || '');
      } catch {
        if (disposed || currentRequest !== requestNumber) return;
        setTariff(null);
        setLoadError(true);
      } finally {
        if (!disposed && currentRequest === requestNumber) {
          clearTimeout(timeout);
          setLoading(false);
        }
      }
    }
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onFocus = () => { void refresh(); };
    void refresh();
    const interval = setInterval(onVisible, 30000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      controller?.abort();
      clearTimeout(timeout);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [retry]);

  const services = (tariff?.services || []).filter((service) => service.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  let result: ReturnType<typeof calculateStorageFee> | null = null;
  let inputError = '';
  if (!loading && tariff && services.length > 0) {
    try {
      if (!/^\d+$/.test(quantity)) throw new Error('Konteyner adedi pozitif bir tam sayı olmalıdır.');
      result = calculateStorageFee(tariff, {
        serviceKey,
        containerSize: size,
        startDate,
        endDate,
        quantity: Number(quantity),
      });
    } catch (error) {
      inputError = error instanceof Error ? error.message : 'Hesaplama bilgilerini kontrol edin.';
    }
  }
  const bandLabel = (fromDay: number, toDay: number | null) =>
    toDay === null ? `${fromDay}. ${t('gün ve sonrası')}` : `${fromDay}–${toDay} ${t('gün arası')}`;
  // Dokum satiri: kalisin bu dilime dusen gunleri (ornek: 8–9. gun / Days 8–9)
  const lineLabel = (fromDay: number, toDay: number) => {
    if (locale === 'en') return fromDay === toDay ? `Day ${fromDay}` : `Days ${fromDay}–${toDay}`;
    return fromDay === toDay ? `${fromDay}. gün` : `${fromDay}–${toDay}. gün`;
  };

  return (
    <section ref={sectionRef} className="calculator-section storage-calculator" id="ardiye-hesaplayici" aria-labelledby="storage-calculator-title">
      <div className="container">
        <div className="calculator-shell">
          <div className="calculator-form-panel">
            <div className="calculator-eyebrow">{t('TAHMİNİ HESAPLAMA')}</div>
            <h2 className="calculator-title" id="storage-calculator-title">{t('Ardiye Hesaplayıcı')}</h2>
            <p className="calculator-description">{t('Girdiğiniz bilgiler kaydedilmez. Sonuç tahmin niteliğindedir; fatura için resmi tarife esas alınır.')}</p>
            <div className="calculator-group">
              <span className="calculator-label" id="storage-size-label">{t('Konteyner Tipi')}</span>
              <div className="calculator-size-grid" role="group" aria-labelledby="storage-size-label">
                {CONTAINERS.map((option) => (
                  <button type="button" key={option.value} aria-pressed={size === option.value}
                    className={`calculator-size-card ${size === option.value ? 'active' : ''}`}
                    onClick={() => setSize(option.value)}>
                    <span className="calculator-size-title">{option.title}</span>
                    <span className="calculator-size-subtitle">{t(option.subtitle)}</span>
                    {size === option.value && <span className="calculator-size-check" aria-hidden="true">✓</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="calculator-group">
              <span className="calculator-label">{t('Liman Giriş ve Tahmini Çıkış Tarihi')}</span>
              <div className="calculator-date-grid">
                <label className="calculator-date-field">
                  <span className="calculator-date-icon"><img src={iconCalendar} alt="" /></span>
                  <input type="date" aria-label={t('Liman giriş tarihi')} value={startDate}
                    onChange={(event) => setStartDate(event.target.value)} />
                </label>
                <label className="calculator-date-field">
                  <span className="calculator-date-icon"><img src={iconCalendar} alt="" /></span>
                  <input type="date" aria-label={t('Tahmini çıkış tarihi')} value={endDate}
                    onChange={(event) => setEndDate(event.target.value)} />
                </label>
              </div>
              <small className="storage-calculator-hint">{t('Giriş ve çıkış günleri hesaba dahildir.')}</small>
            </div>
            <div className="calculator-group">
              <span className="calculator-label" id="storage-service-label">{t('Hizmet Türü')}</span>
              <div className="calculator-service-toggle" role="group" aria-labelledby="storage-service-label">
                {services.map((service) => (
                  <button key={service.key} type="button" aria-pressed={serviceKey === service.key}
                    className={serviceKey === service.key ? 'active' : ''}
                    onClick={() => setServiceKey(service.key)}>{t(service.name)}</button>
                ))}
              </div>
              {loading && <p className="storage-calculator-status" role="status">{t('Tarife yükleniyor…')}</p>}
              {loadError && <div className="storage-calculator-status" role="alert">
                <p>{t('Güncel tarife alınamadı. Bağlantınızı kontrol edip tekrar deneyin.')}</p>
                <button type="button" onClick={() => setRetry((value) => value + 1)}>{t('Tekrar dene')}</button>
              </div>}
              {!loading && !loadError && services.length === 0 && <p className="storage-calculator-status" role="status">{t('Şu anda hesaplamaya açık hizmet bulunmuyor.')}</p>}
            </div>
            <div className="calculator-group">
              <label className="calculator-label" htmlFor="storage-quantity">{t('Konteyner Adedi')}</label>
              <div className="calculator-quantity-field">
                <span className="calculator-quantity-icon"><img src={iconHexagon} alt="" /></span>
                <input id="storage-quantity" type="number" min={1} step={1} value={quantity}
                  onChange={(event) => setQuantity(event.target.value)} />
              </div>
            </div>
            {inputError && <p role="alert" className="storage-calculator-validation">{t(inputError)}</p>}
            <button type="button" className="calculator-submit" disabled={!result}
              onClick={() => resultPanel.current?.focus()}>{t('Ücreti Hesapla')} <span aria-hidden="true">→</span></button>
          </div>
          <div className="calculator-result-panel" ref={resultPanel} tabIndex={-1} aria-live="polite" aria-busy={loading}>
            <div className="calculator-result-eyebrow">{t('TAHMİNİ TOPLAM ÜCRET')}</div>
            {result ? <>
              <div className="calculator-result-meta">{result.quantity} × {size === '20' ? "20'" : "40'/45'"} {t(result.service.name)} · {result.days} {t('Gün')}</div>
              <div className="calculator-total-row"><strong data-testid="storage-total">{money(result.grossTotal)}</strong><span>USD</span></div>
              <div className="storage-calculator-total-note">{result.service.vatPercent > 0 ? t('KDV dahil') : t('Hesaplanan toplam')}</div>
              <div className="calculator-breakdown">
                {result.lines.map((line) => (
                  <div className="calculator-breakdown-item" key={line.fromDay}>
                    <div><b>{lineLabel(line.fromDay, line.toDay)}</b>
                      <small>{line.rate === 0 ? 'FREE · ' : ''}{line.days} {t('gün')} × {money(line.rate)} USD × {result!.quantity} {t('Konteyner')}</small>
                    </div>
                    <strong>{money(line.amount)} USD</strong>
                  </div>
                ))}
                <div className="calculator-breakdown-item storage-calculator-tax"><span>{t('Ara toplam')}</span><strong>{money(result.netTotal)} USD</strong></div>
                <div className="calculator-breakdown-item storage-calculator-tax"><span>{t('KDV')} %{result.service.vatPercent}</span><strong>{money(result.vatAmount)} USD</strong></div>
              </div>
              <p className="storage-calculator-rule">{t('Her gün, düştüğü dilimin günlük fiyatıyla ücretlendirilir; dilim tutarları toplanır ve konteyner adediyle çarpılır.')}</p>
              <div className="calculator-tier-bar" style={{ gridTemplateColumns: `repeat(${Math.min(result.service.bands.length, 4)}, minmax(0, 1fr))` }} aria-hidden="true">
                {/* Gecilen dilimler mavi, kalisin bittigi dilim kirmizi */}
                {result.service.bands.map((band) => <span key={band.fromDay} className={
                  band.fromDay === result!.band.fromDay ? 'active' : band.fromDay < result!.band.fromDay ? 'done' : 'idle'} />)}
              </div>
              <div className="calculator-tier-labels" style={{ gridTemplateColumns: `repeat(${Math.min(result.service.bands.length, 4)}, minmax(0, 1fr))` }}>
                {result.service.bands.map((band) => <span key={band.fromDay}>{bandLabel(band.fromDay, band.toDay)}</span>)}
              </div>
            </> : <p className="storage-calculator-result-placeholder">{t(loading ? 'Güncel tarife yükleniyor…' : loadError ? 'Hesaplama için güncel tarife gereklidir.' : inputError ? 'Geçerli tarih ve konteyner adedi girin.' : 'Hizmet tarifesi kullanıma açıldığında hesaplama yapılabilir.')}</p>}
            <div className="calculator-actions">
              <button type="button" className="calculator-action-outline" disabled={!result || pdfBusy} aria-busy={pdfBusy}
                onClick={handlePdf}>{t(pdfBusy ? 'PDF hazırlanıyor…' : 'PDF İndir')}</button>
              <Link to="/iletisim" className="calculator-action-solid">{t('Teklif Al')}</Link>
            </div>
            {pdfError && <p role="alert" className="storage-calculator-pdf-error">{t('PDF oluşturulamadı. Lütfen tekrar deneyin.')}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
